import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users, CalendarCheck, Clock, CheckCircle, Warning, XCircle, QrCode, Robot, X } from "@phosphor-icons/react";
import api, { todayStr } from "../lib/api";
import StatusBadge from "../components/StatusBadge";

export default function Oggi() {
  const [stats, setStats] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [briefingOff, setBriefingOff] = useState(
    () => localStorage.getItem(`briefing_hide_${todayStr()}`) === "1");
  const day = todayStr();

  useEffect(() => {
    api.post("/automations/run-due").catch(() => {});
    api.get(`/stats/today?date=${day}`).then((r) => setStats(r.data)).catch(() => {});
    api.get("/assistant/briefing").then((r) => setBriefing(r.data)).catch(() => {});
  }, [day]);

  const dismissBriefing = () => {
    localStorage.setItem(`briefing_hide_${todayStr()}`, "1");
    setBriefingOff(true);
  };

  const dateLabel = new Date().toLocaleDateString("it-IT", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  const metrics = stats ? [
    { label: "Prenotazioni", value: stats.prenotazioni, icon: CalendarCheck, testid: "metric-prenotazioni" },
    { label: "Coperti", value: stats.coperti, icon: Users, testid: "metric-coperti" },
    { label: "Da confermare", value: stats.da_confermare, icon: Clock, testid: "metric-da-confermare", accent: stats.da_confermare > 0 },
    { label: "Arrivati (IN)", value: stats.arrivati, icon: CheckCircle, testid: "metric-arrivati" },
    { label: "Ancora attesi", value: stats.attesi, icon: Clock, testid: "metric-attesi" },
    { label: "No-show", value: stats.no_show, icon: XCircle, testid: "metric-noshow", danger: stats.no_show > 0 },
  ] : [];

  return (
    <div className="space-y-6" data-testid="oggi-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Oggi</h1>
          <p className="text-sm text-zinc-500 capitalize" data-testid="oggi-data">{dateLabel}</p>
        </div>
        <Link to="/scanner" data-testid="btn-scanner-qr"
          className="h-11 px-4 rounded-md border border-zinc-300 bg-white text-sm font-semibold flex items-center gap-2 text-zinc-700 hover:bg-zinc-50 active:scale-95 transition shrink-0">
          <QrCode size={16} weight="bold" /> Scanner QR
        </Link>
      </div>

      {briefing && !briefingOff && (
        <div className="bg-gradient-to-br from-sky-50 to-emerald-50 border border-sky-200 rounded-lg p-4 flex items-start gap-3"
          data-testid="briefing-card">
          <span className="w-9 h-9 rounded-lg bg-white border border-sky-200 flex items-center justify-center shrink-0 text-sky-600">
            <Robot size={18} weight="bold" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-sky-700">Briefing del giorno</p>
            <p className="text-sm text-zinc-800 mt-1 leading-relaxed" data-testid="briefing-text">{briefing.text}</p>
            <button data-testid="briefing-action"
              onClick={() => window.dispatchEvent(new CustomEvent("assistant:ask", { detail: briefing.suggested_prompt }))}
              className="mt-2 h-8 px-3 rounded-md bg-zinc-900 text-white text-xs font-bold hover:bg-zinc-700 active:scale-95 transition">
              Esegui con l'assistente
            </button>
          </div>
          <button onClick={dismissBriefing} data-testid="briefing-dismiss"
            className="text-zinc-400 hover:text-zinc-600 transition-colors shrink-0" title="Nascondi per oggi">
            <X size={14} />
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {metrics.map(({ label, value, icon: Icon, testid, accent, danger }) => (
          <div key={label} data-testid={testid}
            className={`bg-white border rounded-lg p-4 ${danger ? "border-rose-300" : accent ? "border-amber-300" : "border-zinc-200"}`}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-zinc-500">
              <Icon size={13} weight="bold" /> {label}
            </div>
            <div className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-zinc-900 mt-1">{value}</div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <section className="bg-white border border-zinc-200 rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
            <h2 className="font-display font-semibold text-zinc-900">Prossimi arrivi</h2>
            <Link to="/prenotazioni" data-testid="link-tutte-prenotazioni"
              className="text-xs font-semibold text-zinc-900 flex items-center gap-1 hover:underline">
              Vedi tutte <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
          <div className="divide-y divide-zinc-100">
            {stats?.prossimi_arrivi?.length ? stats.prossimi_arrivi.map((r) => (
              <Link key={r.id} to={`/prenotazioni/${r.id}`} data-testid={`next-arrival-${r.id}`}
                className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors">
                <span className="font-display font-bold text-lg text-zinc-900 w-14">{r.time}</span>
                <span className="flex-1 text-sm font-medium text-zinc-900 truncate">{r.customer_name}</span>
                <span className="text-xs text-zinc-500">{r.party_size} pers.</span>
                <StatusBadge status={r.status} />
              </Link>
            )) : (
              <div className="px-4 py-8 text-sm text-zinc-400" data-testid="no-next-arrivals">Nessun arrivo in programma</div>
            )}
          </div>
        </section>

        <section className="bg-white border border-zinc-200 rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
            <h2 className="font-display font-semibold text-zinc-900">Situazione sala</h2>
            <Link to="/sala" data-testid="link-sala"
              className="text-xs font-semibold text-zinc-900 flex items-center gap-1 hover:underline">
              Mappa sala <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
          <div className="divide-y divide-zinc-100">
            {stats?.sale?.map((s) => (
              <div key={s.name} className="px-4 py-3" data-testid={`sala-${s.name}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-medium text-zinc-900">{s.name}</span>
                  <span className="text-xs text-zinc-500">{s.occupati} occupati · {s.riservati} riservati · {s.tavoli} tavoli</span>
                </div>
                <div className="h-2 rounded-full bg-zinc-100 overflow-hidden flex">
                  <div className="bg-blue-500 h-full transition-all" style={{ width: `${s.tavoli ? (s.occupati / s.tavoli) * 100 : 0}%` }} />
                  <div className="bg-amber-400 h-full transition-all" style={{ width: `${s.tavoli ? (s.riservati / s.tavoli) * 100 : 0}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="px-4 py-3 border-t border-zinc-200 flex gap-4 text-xs text-zinc-500">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-blue-500 inline-block" /> Occupato</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-amber-400 inline-block" /> Riservato</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-zinc-200 inline-block" /> Libero</span>
          </div>
        </section>
      </div>

      {stats && stats.out > 0 && (
        <div className="text-sm text-zinc-500" data-testid="oggi-out-count">
          Servizio completato per <strong className="text-zinc-900">{stats.out}</strong> prenotazioni oggi.
        </div>
      )}
    </div>
  );
}
